(function initializeVoyadoDrawerSearch(window, document) {
    'use strict';

    if (
        customElements.get('voyado-predictive-search') ||
        !window.VOYADO_CONTEXT?.enabled ||
        !window.VOYADO_ELEVATE?.createClient
    ) {
        return;
    }

    const searchConfiguration = window.VOYADO_CONTEXT?.search ?? {};
    const DEBOUNCE_MS = 50;
    const PDP_PRODUCT_KEY_PARAMETER = 'voyadoProductKey';

    class VoyadoPredictiveSearch extends HTMLElement {
        constructor() {
            super();

            this.client = window.VOYADO_ELEVATE.createClient();
            this.input = this.querySelector('input[type="search"]');
            this.form = this.input?.form;
            this.resetButton = this.querySelector('button[type="reset"]');
            this.results = this.querySelector('[data-predictive-search]');
            this.status = this.querySelector('.predictive-search-status');
            this.requestController = null;
            this.requestId = 0;
            this.timer = null;
            this.response = null;
            this.responseQuery = '';
            this.optionId = 0;

            if (!this.input || !this.form || !this.results) return;

            this.input.addEventListener('input', () => this.onInput());
            this.input.addEventListener('focus', () => this.onFocus());
            this.form.addEventListener('reset', (event) => this.onReset(event));
            this.form.addEventListener('submit', (event) => this.onSubmit(event));
            this.addEventListener('click', (event) => this.onClick(event));
            this.addEventListener('keydown', (event) => this.onKeydown(event));

            this.toggleResetButton();
        }

        query() {
            return this.input.value.trim();
        }

        onInput() {
            this.toggleResetButton();
            this.scheduleRequest();
        }

        onFocus() {
            const query = this.query();

            if (this.response && query === this.responseQuery) {
                this.render();
                return;
            }

            this.scheduleRequest();
        }

        onReset(event) {
            event.preventDefault();
            window.clearTimeout(this.timer);
            this.requestController?.abort();
            this.input.value = '';
            this.response = null;
            this.responseQuery = '';
            this.toggleResetButton();
            this.input.focus();
            this.scheduleRequest();
        }

        onSubmit(event) {
            const query = this.query();

            if (!query) {
                event.preventDefault();
                return;
            }

            if (query !== this.responseQuery || !this.response?.redirectLink) return;

            const redirectUrl = this.safeUrl(this.response.redirectLink);
            if (!redirectUrl) return;

            if (
                this.response.exactHit?.key &&
                redirectUrl.origin === window.location.origin
            ) {
                redirectUrl.searchParams.set(
                    PDP_PRODUCT_KEY_PARAMETER,
                    this.response.exactHit.key
                );
            }

            event.preventDefault();
            window.location.assign(redirectUrl.toString());
        }

        onClick(event) {
            const suggestion = event.target.closest('[data-voyado-search-query]');
            if (!suggestion) return;

            const query = suggestion.dataset.voyadoSearchQuery?.trim();
            if (!query) return;

            event.preventDefault();
            window.location.assign(
                this.searchUrl(query, suggestion.dataset.voyadoSearchOrigin)
            );
        }

        onKeydown(event) {
            if (event.key === 'Escape') {
                this.close();
                this.input.focus();
                return;
            }

            if (!this.hasAttribute('open')) return;

            if (event.key === 'Enter') {
                const selected = this.querySelector('[role="option"][aria-selected="true"]');
                const action = selected?.querySelector('a, button');

                if (action) {
                    event.preventDefault();
                    action.click();
                }
                return;
            }

            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

            const options = [...this.querySelectorAll('[role="option"]')]
                .filter((option) => option.offsetParent !== null);
            if (!options.length) return;

            event.preventDefault();
            const selected = this.querySelector('[role="option"][aria-selected="true"]');
            const selectedIndex = options.indexOf(selected);
            const direction = event.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = selectedIndex === -1
                ? (direction === 1 ? 0 : options.length - 1)
                : (selectedIndex + direction + options.length) % options.length;

            selected?.setAttribute('aria-selected', 'false');
            options[nextIndex].setAttribute('aria-selected', 'true');
            this.input.setAttribute('aria-activedescendant', options[nextIndex].id);
        }

        scheduleRequest() {
            window.clearTimeout(this.timer);
            const query = this.query();

            this.timer = window.setTimeout(
                () => this.load(query),
                DEBOUNCE_MS
            );
        }

        configuration(query) {
            if (!query) return {};

            const contentListIds = searchConfiguration.contentListIds ?? [];

            return {
                contentLists: Array.isArray(contentListIds)
                ? contentListIds.map((id) => String(id).trim()).filter(Boolean).map((id) => ({
                    id: String(id),
                }))
                    : [],
            };
        }

        async load(query) {
            this.requestController?.abort();
            const requestId = ++this.requestId;

            this.requestController = new AbortController();
            const controller = this.requestController;

            this.setLoading(true);

            try {
                const response = await this.client.autocomplete(
                    { q: query },
                    this.configuration(query),
                    { signal: controller.signal }
                );

                if (requestId !== this.requestId) return;

                this.response = response;
                this.responseQuery = query;
                this.render();
            } catch (error) {
                if (requestId === this.requestId && !controller.signal.aborted) {
                    console.warn('[Voyado drawer search] Autocomplete failed', error);
                    this.close();
                }
            } finally {
                if (requestId === this.requestId) this.setLoading(false);
            }
        }

        render() {
            if (this.query() !== this.responseQuery || !this.response) {
                this.close();
                return;
            }

            this.optionId = 0;
            const query = this.responseQuery;
            const fragment = document.createDocumentFragment();
            const recent = query
                ? []
                : (this.response.recentSearches || []);
            const phrases = this.response.phraseSuggestions || [];
            const products = this.products();
            const content = (this.response.contentLists || [])
                .flatMap((list) => list?.items || []);

            this.appendSection(fragment, this.dataset.recentLabel, recent, (item) =>
                this.phraseOption(item.q, '', 'RECENT_SEARCH')
            );
            this.appendSection(fragment, this.dataset.suggestionsLabel, phrases, (item) =>
                this.phraseOption(item.q, item.highlighted, 'PHRASE_SUGGEST')
            );
            this.appendSection(fragment, this.dataset.productsLabel, products, (product) =>
                this.resultOption(product, 'product')
            );
            this.appendSection(fragment, this.dataset.pagesLabel, content, (item) =>
                this.resultOption(item, 'content')
            );

            if (query) fragment.append(this.searchOption(query));

            this.results.replaceChildren(fragment);

            const count = this.results.querySelectorAll('[role="option"]').length;
            if (count) this.open(count);
            else this.close();
        }

        products() {
            const groups = Array.isArray(this.response?.productSuggestions)
                ? this.response.productSuggestions
                : [];
            const products = groups.map((group) => group?.products?.[0]).filter(Boolean);
            const exactHit = this.response?.exactHit;

            if (exactHit && !products.some((product) => product.key === exactHit.key)) {
                products.unshift(exactHit);
            }

            return products;
        }

        appendSection(fragment, label, items, createOption) {
            const options = items.map(createOption).filter(Boolean);
            if (!options.length) return;

            const section = document.createElement('div');
            const heading = document.createElement('h4');
            const list = document.createElement('ul');
            const headingId = `voyado-drawer-heading-${this.optionId}`;

            section.className = 'predictive-search__result-group';
            heading.id = headingId;
            heading.className = 'predictive-search__heading h6';
            heading.textContent = label || '';
            list.className = 'predictive-search__results-list list-unstyled';
            list.setAttribute('role', 'group');
            list.setAttribute('aria-labelledby', headingId);
            list.append(...options);
            section.append(heading, list);
            fragment.append(section);
        }

        phraseOption(query, highlighted, origin) {
            if (!query) return null;

            const option = this.option();
            const button = document.createElement('button');
            const text = document.createElement('span');

            button.type = 'button';
            button.className = 'predictive-search__item link';
            button.dataset.voyadoSearchQuery = query;
            button.dataset.voyadoSearchOrigin = origin;
            text.className = 'predictive-search__item-query-result';
            this.appendHighlightedText(text, highlighted, query);
            button.append(text);
            option.append(button);
            return option;
        }

        resultOption(result, type) {
            if (!result?.link) return null;

            const url = this.safeUrl(result.link);
            if (!url) return null;

            if (type === 'product' && result.key && url.origin === window.location.origin) {
                url.searchParams.set(PDP_PRODUCT_KEY_PARAMETER, result.key);
            }

            const option = this.option();
            const link = document.createElement('a');
            const content = document.createElement('div');
            const title = document.createElement('p');

            link.href = url.toString();
            link.className = 'predictive-search__item';
            link.tabIndex = -1;
            link.dataset.voyadoResultType = type;
            content.className = 'predictive-search__item-content predictive-search__item-content--centered';
            title.className = 'predictive-search__item-heading';
            title.textContent = result.title || result.name || '';

            if (window.VOYADO_NOTIFICATIONS && result.ticket) {
                link.dataset.voyadoTicket = result.ticket;
            }

            const imageUrl = result.imageInfo?.thumbnail || result.image?.sources?.[0]?.url;
            if (imageUrl) {
                const image = document.createElement('img');
                image.className = 'predictive-search__image';
                image.src = imageUrl;
                image.alt = result.imageInfo?.images?.[0]?.alt || result.image?.alt || '';
                image.width = 50;
                image.height = 50;
                image.loading = 'lazy';
                link.classList.add('predictive-search__item--link-with-thumbnail');
                link.append(image);
            }

            content.append(title);
            link.append(content);
            option.append(link);
            return option;
        }

        searchOption(query) {
            const option = this.option('predictive-search__search-for-button');
            const link = document.createElement('a');
            const label = (this.dataset.searchForLabel || '')
                .replace('[terms]', query);

            link.href = this.searchUrl(query, 'ORGANIC');
            link.className = 'predictive-search__item predictive-search__item--term btn btn--with-icon btn--with-icon-last';
            link.tabIndex = -1;
            link.textContent = label;
            option.append(link);
            return option;
        }

        option(className = 'predictive-search__list-item') {
            const option = document.createElement('li');
            option.id = `voyado-drawer-option-${++this.optionId}`;
            option.className = className;
            option.setAttribute('role', 'option');
            option.setAttribute('aria-selected', 'false');
            return option;
        }

        appendHighlightedText(element, highlighted, fallback) {
            const value = String(highlighted || '');

            if (!value.includes('{')) {
                element.textContent = fallback;
                return;
            }

            for (const match of value.matchAll(/\{([^{}]*)\}|([^{}]+)/g)) {
                if (match[1] !== undefined) {
                    const mark = document.createElement('mark');
                    mark.textContent = match[1];
                    element.append(mark);
                } else if (match[2]) {
                    element.append(document.createTextNode(match[2]));
                }
            }
        }

        safeUrl(value) {
            try {
                const url = new URL(value, window.location.origin);
                return ['http:', 'https:'].includes(url.protocol) ? url : null;
            } catch {
                return null;
            }
        }

        searchUrl(query, origin) {
            const url = new URL(window.routes.search_url, window.location.origin);
            url.searchParams.set('q', query);
            if (origin && origin !== 'ORGANIC') url.searchParams.set('origin', origin);
            return url.toString();
        }

        toggleResetButton() {
            this.resetButton?.classList.toggle('hidden', !this.query());
        }

        setLoading(isLoading) {
            this.toggleAttribute('loading', isLoading);
            if (!isLoading || !this.status) return;

            this.status.textContent = this.dataset.loadingText || '';
            this.status.setAttribute('aria-hidden', 'false');
        }

        open(count) {
            this.setAttribute('open', 'true');
            this.input.setAttribute('aria-expanded', 'true');

            if (this.status) {
                this.status.textContent = (this.dataset.statusLabel || '')
                    .replace('[count]', count);
                this.status.setAttribute('aria-hidden', 'false');
                window.setTimeout(
                    () => this.status?.setAttribute('aria-hidden', 'true'),
                    1000
                );
            }
        }

        close() {
            this.removeAttribute('open');
            this.input.setAttribute('aria-expanded', 'false');
            this.input.removeAttribute('aria-activedescendant');
            this.querySelector('[role="option"][aria-selected="true"]')
                ?.setAttribute('aria-selected', 'false');
        }
    }

    customElements.define('voyado-predictive-search', VoyadoPredictiveSearch);
})(window, document);
